import React, { useEffect, useRef, useState } from 'react';
import EditModalTemplate from '@component/gridgio/modal/dialog-modal/edit-modal-template';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useModifyPlanMutation } from '@hook/gridgio/useApi';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import { isPlanEditModalDataType, type IPlanEditModalData } from '@datatype/gridgio/type';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import Gap from '@component/common/gap';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  data: IPlanEditModalData['data'];
}

const PlanEditModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const mutation = useModifyPlanMutation();
  const [name, setName] = useState(data.name);
  const isChanged = name.trim() !== data.name;
  const [errorMessage, setErrorMessage] = useState('');
  const dialogModalData = useTypedSelector((state) => state.rootReducer?.gridgioModalReducer?.dialogModalData) ?? null;
  const currentModalRef = useRef(dialogModalData);
  currentModalRef.current = dialogModalData;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleCancel = () => {
    if (!mutation.isPending) dispatch(modalActions.hideModal());
  };
  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !isChanged || mutation.isPending) return;
    setErrorMessage('');
    try {
      await mutation.mutateAsync({
        projectKey: data.projectKey,
        planKey: data.planKey,
        name: trimmedName,
      });
      const currentModal = currentModalRef.current;
      if (mountedRef.current && isPlanEditModalDataType(currentModal) && currentModal.data === data) {
        dispatch(modalActions.hideModal());
      }
    } catch {
      setErrorMessage('도면을 수정하지 못했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <EditModalTemplate
      title='도면 편집'
      errorMessage={errorMessage}
      isPending={mutation.isPending}
      saveDisabled={!name.trim() || !isChanged}
      handleCancel={handleCancel}
      handleSave={() => void handleSave()}
      input={
        <>
          <TextBlock
            text='도면 이름'
            design={{
              box: { width: '100%', alignX: 'left' },
              text: { size: 'medium', color: color.BLACK_2, bold: true },
            }}
          />
          <Gap height={size.MARGIN_1} />
          <TextInput
            name='plan-name'
            value={name}
            placeholder='도면 이름을 입력해주세요'
            design={{
              box: {
                width: '100%',
                height: '42px',
                padding: `0 ${size.MARGIN_1}`,
                border: true,
                borderWidth: size.BORDER_WIDTH_1,
                borderColor: color.GRAY_2,
                borderRadius: size.BORDER_RADIUS_1,
                alignX: 'left',
              },
              text: { size: 'medium', color: color.BLACK_2 },
            }}
            onChange={(event) => {
              if (!mutation.isPending) setName(event.target.value);
            }}
          />
        </>
      }
    />
  );
};

export default PlanEditModal;
